import { faAdd, faAngleDown, faAngleRight, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Button, Card, Col, Form, ListGroup, Row, Stack } from "react-bootstrap";
import { Goal } from "../../../types/goal";
import useApp from "../../../utils/useApp";
import {
  deleteGoalTemplate,
  deleteTaskTemplate,
  patchGoalTemplate,
  patchTaskTemplate,
  postTaskTemplate,
  reorderTaskTemplate,
} from "../../../services/httpClient";
import { unsavedChanges } from "../../../App";
import Input from "../../ui/Input";
import IconButton from "../../ui/IconButton";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";
import ReorderEditDeleteCards from "../../ui/ReorderEditDeleteCards";

interface GoalTemplateState {
  goal: Goal;
  getGoalTemplates: any;
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  setErrMsg: any;
}

const GoalTemplateCard = ({
  goal,
  getGoalTemplates,
  setOrgToSave,
  setShowToast,
  setToastText,
  setToastBody,
  setErrMsg,
}: GoalTemplateState) => {
  const { auth }: any = useApp();
  const [isEditing, setIsEditing] = useState(false);
  const [isTaskEditing, setIsTaskEditing] = useState(false);
  const [allowSaveGoal, setAllowSaveGoal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<Goal>();
  const [goalName, setGoalName] = useState<string>(goal?.name);
  const [showAddTask, setShowAddTask] = useState<boolean>(false);
  const [newTask, setNewTask] = useState<string>("");
  const [showTasks, setShowTasks] = useState<boolean>(false);
  const [allowSaveTask, setAllowSaveTask] = useState<boolean>(false);

  const onClickEdit = () => {
    setIsEditing(true);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
    setGoalToDelete(goal);
  };

  const onClickDeleteFinal = () => {
    setErrMsg("");
    deleteGoalTemplate(auth?.profile?.userOrgID, goalToDelete?.id)
      .then((res: any) => {
        setIsDeleting(false);
        setGoalToDelete(undefined);
        getGoalTemplates();
        setShowToast(true);
        setToastText("Goal Template Deleted");
        setToastBody(goalToDelete?.name);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setIsEditing(false);
    setIsTaskEditing(false);
    setGoalToDelete(undefined);
  };

  const onClickSave = () => {
    if (!goalName) {
      setErrMsg("Cannot set null goal name");
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
      return;
    }
    const updatedGoal = {
      name: goalName,
    };

    setErrMsg("");

    patchGoalTemplate(updatedGoal, auth?.profile?.userOrgID, goal?.id)
      .then((res: any) => {
        setIsEditing(false);
        setAllowSaveGoal(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getGoalTemplates(false);
        setShowToast(true);
        setToastText("Goal Template Updated");
        setToastBody(goalName);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancel = () => {
    setErrMsg("");
    setIsEditing(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const updateGoalName = (e: any) => {
    setGoalName(e.target.value);
    setAllowSaveGoal(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickAddTask = () => {
    setShowAddTask(true);
    setShowTasks(true);
  };

  const updateTask = (e: any) => {
    setNewTask(e.target.value);
    setAllowSaveTask(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const onClickSaveTask = () => {
    const task = {
      name: newTask,
    };

    setErrMsg("");

    postTaskTemplate(task, auth?.profile?.userOrgID, goal?.id)
      .then((res: any) => {
        setShowAddTask(false);
        setNewTask("");
        setAllowSaveTask(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getGoalTemplates();
        setShowToast(true);
        setToastText("Task Created");
        setToastBody(newTask);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelTask = () => {
    setShowAddTask(false);
    setNewTask("");
    setAllowSaveTask(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const onClickGoalSubmit = (e: any) => {
    e?.preventDefault();
    onClickSave();
  };

  const onClickTaskSubmit = (e: any) => {
    e?.preventDefault();
    onClickSaveTask();
  };

  const onClickEditTaskFinal = (taskId: any, taskName: string) => {
    const updatedTask = {
      name: taskName,
    };

    setErrMsg("");

    patchTaskTemplate(updatedTask, auth?.profile?.userOrgID, goal?.id, taskId)
      .then((res: any) => {
        setAllowSaveTask(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        setIsTaskEditing(false);
        setIsEditing(false);
        getGoalTemplates();
        setShowToast(true);
        setToastText("Task Updated");
        setToastBody(taskName);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const onClickDeleteTaskFinal = (taskId: string, taskName: string) => {
    setErrMsg("");
    deleteTaskTemplate(auth?.profile?.userOrgID, goal?.id, taskId)
      .then((res: any) => {
        setAllowSaveTask(false);
        unsavedChanges.value = false;
        setShowToast(true);
        setToastText("Task Deleted");
        setToastBody(taskName);
        setIsDeleting(false);
        getGoalTemplates();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const updateTaskOrder = (newOrder: any) => {
    reorderTaskTemplate(auth?.profile?.userOrgID, goal?.id, newOrder)
      .then(() => {
        setShowToast(true);
        setToastText("Task Order");
        setToastBody(goal?.name);
        getGoalTemplates(false);
      })
      .catch((err) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const renderDelete = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to delete - {goalToDelete?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      {isDeleting ? (
        <>{renderDelete()}</>
      ) : (
        <Card className="bg-light p-3" key={goal?.id}>
          {isEditing ? (
            <Form onSubmit={(e: any) => onClickGoalSubmit(e)}>
              <Stack direction="horizontal" gap={3} className="mb-3">
                <Stack style={{ justifyContent: "center" }} className="col-6">
                  <Input label="Goal" type="textarea" value={goalName} onUpdate={(e: any) => updateGoalName(e)} />
                </Stack>
                <Button className="button app-secondary-color button" onClick={() => onClickSave()} disabled={!allowSaveGoal}>
                  Save
                </Button>
                <div className="vr" />
                <Button className="button app-primary-color button" onClick={() => onClickCancel()}>
                  Cancel
                </Button>
              </Stack>
            </Form>
          ) : (
            <Stack direction="horizontal" gap={3}>
              <Stack style={{ justifyContent: "center" }} className="col-6">
                <h5 className="app-orange-text">{goal.name}</h5>
              </Stack>
              {isEditing || isTaskEditing || showAddTask ? (
                <>
                  <FontAwesomeIcon className={"app-light-gray-text"} icon={faEdit} />
                  <div className="vr" />
                </>
              ) : (
                <>
                  <IconButton onClickButton={() => onClickEdit()} tooltipText="Edit Goal" icon={faEdit} />
                  <div className="vr" />
                </>
              )}
              <div>
                <IconButton onClickButton={() => onClickDelete()} tooltipText="Delete Goal" icon={faTrash} />
              </div>
            </Stack>
          )}

          <Row>
            {goal?.tasks?.length > 0 ? (
              <h6 className="cursor-pointer" onClick={() => setShowTasks(!showTasks)}>
                Tasks <FontAwesomeIcon icon={!showTasks ? faAngleRight : faAngleDown} />
              </h6>
            ) : (
              <h6 className="ms-3">Tasks</h6>
            )}
          </Row>
          {showTasks && (
            <Stack gap={2} className="mb-3">
              {goal?.tasks?.length > 0 && (
                <ReorderEditDeleteCards
                  items={goal?.tasks}
                  editInputLabel="Task"
                  onEditFinal={onClickEditTaskFinal}
                  onDeleteFinal={onClickDeleteTaskFinal}
                  updateOrder={updateTaskOrder}
                  setErrMessage={setErrMsg}
                />
              )}
            </Stack>
          )}
          <Row className="mb-0">
            {showAddTask ? (
              <Col xs={12}>
                <Form onSubmit={(e: any) => onClickTaskSubmit(e)}>
                  <Stack direction="horizontal" gap={3}>
                    <Stack style={{ justifyContent: "center" }} className="col-6">
                      <Input label="Enter New Task" type="text" value={newTask} hasFocus={true} onUpdate={(e: any) => updateTask(e)} />
                    </Stack>
                    <Button className="app-secondary-color button" disabled={!allowSaveTask} onClick={() => onClickSaveTask()}>
                      Save
                    </Button>
                    <div className="vr" />
                    <Button className="app-primary-color button" onClick={() => onClickCancelTask()}>
                      Cancel
                    </Button>
                  </Stack>
                </Form>
              </Col>
            ) : (
              <Col xs={12} className="d-flex justify-content-end">
                <IconEmbeddedButton onClickButton={onClickAddTask} tooltipText="Add Task" icon={faAdd} />
              </Col>
            )}
          </Row>
        </Card>
      )}
    </>
  );
};

export default GoalTemplateCard;
